/**
 * `v-reveal` fades and slides an element in when it scrolls into view.
 * Usage: `v-reveal` or `v-reveal="120"` (delay in ms, for staggering).
 *
 * The hidden state is rendered on the server (so there is no flash on load) and
 * is disabled by CSS for reduced motion and by <noscript> for no-JS visitors.
 */
export default defineNuxtPlugin((nuxtApp) => {
  let observer: IntersectionObserver | null = null

  function getObserver () {
    observer ??= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer?.unobserve(entry.target)
        }
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' })
    return observer
  }

  const delayOf = (value: unknown) => (typeof value === 'number' ? value : 0)

  nuxtApp.vueApp.directive<HTMLElement, number | undefined>('reveal', {
    // Runs on the server; the client picks the markup up during hydration
    getSSRProps (binding) {
      return { class: 'reveal', style: { '--reveal-delay': `${delayOf(binding.value)}ms` } }
    },
    created (el, binding) {
      el.classList.add('reveal')
      el.style.setProperty('--reveal-delay', `${delayOf(binding.value)}ms`)
    },
    mounted (el) {
      getObserver().observe(el)
    },
    beforeUnmount (el) {
      observer?.unobserve(el)
    },
  })
})
